/**
 * Footer session control (sc-3015).
 *
 * Owns the `#control-panel-session` slot in the cabinet footer:
 *   - Signed out → "SIGN IN" button that opens the login modal
 *   - Signed in  → "PLAYER: email · ACCOUNT · LOGOUT"
 *
 * Hooks the session-change event from auth/session.ts so the UI
 * flips immediately on login/logout — no reload.
 */

import { resetAnalytics, track } from '../runtime/analytics.js';
import { showLoginWall } from '../cabinet/play-gate-wall.js';
import { openAccountModal } from './account-modal.js';
import {
  clearSession,
  getSession,
  logoutSession,
  onSessionChange,
  type Session,
} from '@arcraz/arcadia-auth-web';

export function wireFooterSession(): void {
  const slot = document.getElementById('control-panel-session');
  if (!(slot instanceof HTMLElement)) {
    // eslint-disable-next-line no-console
    console.warn('[auth] footer slot missing — skipping session UI');
    return;
  }

  const render = (session: Session | null): void => {
    // Clear children — safer than innerHTML and respects accessibility tree.
    while (slot.firstChild) slot.removeChild(slot.firstChild);

    if (session) {
      const label = document.createElement('span');
      label.className = 'control-panel__player-email';
      label.textContent = `PLAYER: ${session.email}`;
      slot.appendChild(label);

      slot.appendChild(document.createTextNode(' · '));

      // Export / delete live behind here (sc-8410). Placed before LOGOUT
      // so the destructive one is not the button nearest the thumb.
      const account = document.createElement('button');
      account.type = 'button';
      account.className = 'control-panel__logout';
      account.textContent = 'ACCOUNT';
      account.addEventListener('click', () => {
        openAccountModal();
      });
      slot.appendChild(account);

      slot.appendChild(document.createTextNode(' · '));

      const logout = document.createElement('button');
      logout.type = 'button';
      logout.className = 'control-panel__logout';
      logout.textContent = 'LOGOUT';
      logout.addEventListener('click', () => void doLogout());
      slot.appendChild(logout);
    } else {
      const signIn = document.createElement('button');
      signIn.type = 'button';
      signIn.className = 'control-panel__signin';
      signIn.textContent = 'SIGN IN';
      // Same wall the entry gate shows (Google + email) — one consistent
      // sign-in surface. No cartridge arg: a bare sign-in with no pending
      // launch to relaunch afterward.
      signIn.addEventListener('click', () => {
        track('login_wall_shown', { source: 'footer' });
        void showLoginWall();
      });
      slot.appendChild(signIn);
    }
  };

  render(getSession());
  onSessionChange(render);
}

/**
 * Logout: clear local session immediately (player sees UI flip),
 * then fire revoke server-side as a best-effort cleanup. We don't
 * await or block on revoke — if Zyphr is down, the player is still
 * locally logged out, and the next refresh attempt will fail and
 * fully clear any leftover state.
 */
async function doLogout(): Promise<void> {
  const session = getSession();
  clearSession();
  resetAnalytics();
  track('auth_signout');
  if (!session) return;
  try {
    await logoutSession(session.refreshToken);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[auth] revoke failed (local logout still complete)', err);
  }
}
